import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_ASSIGNMENTS } from '@/lib/constants/api'
import getAssignmentModel from './models/assignment'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const assignment = {
  namespaced: true,
  state: {
    ...getAssignmentModel()
  },
  getters: {
    getField,
    getAssignmentObject: (state) => {
      return state
    }
  },
  mutations: {
    updateField,
    updateStatus,
    // Reset from the model rather than by hand, so a new field only has to be added in one
    // place and can't drift out of sync with the model's own defaults.
    clearAssignment: (state) => {
      Object.assign(state, getAssignmentModel())
    },
    setAssignment: (state, assignment) => {
      state.id = assignment.id
      state.userId = assignment.userId
      state.billRate = assignment.billRate
      state.startAt = assignment.startAt
      state.endAt = assignment.endAt
      state.contractTypeId = assignment.contractTypeId
      state.lengthOfContract = assignment.lengthOfContract
      state.conversionFee = assignment.conversionFee
      state.employeeTypeId = assignment.employeeTypeId
      state.corporateName = assignment.corporateName
      state.active = assignment.active
      state.clientId = assignment.clientId
      state.overtimeEligible = assignment.overtimeEligible
      state.billRateTypeId = assignment.billRateTypeId
      state.payFrequencyId = assignment.payFrequencyId
      state.payRateTypeId = assignment.payRateTypeId
      state.payRate = assignment.payRate
      state.ein = assignment.ein
      state.commissions = assignment.commissions || []
      state.expenses = assignment.expenses || []
      state.timeTypeRates = assignment.timeTypeRates || []
      state.vmsFee = assignment.vmsFee
      state.workedHoursAverage = assignment.workedHoursAverage
      state.jobTitle = assignment.jobTitle
      state.timeSheetType = assignment.timeSheetType
      state.clockIPAddress = assignment.clockIPAddress
      state.overtimeThreshold = assignment.overtimeThreshold
      state.overtimeRate = assignment.overtimeRate
      state.contractorCommissionEnabled = assignment.contractorCommissionEnabled
      state.contractorCommissionMarkup = assignment.contractorCommissionMarkup
      state.vmsAssignmentId = assignment.vmsAssignmentId
    }
  },
  actions: {
    save ({ state }) {
      return api.doPost({ path: PATH_ASSIGNMENTS, data: state })
    },
    update ({ state }) {
      return api.doPut({ path: PATH_ASSIGNMENTS, data: state })
    },
    getAssignment: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting contract, please wait...' })

      return api.doGet({ path: `${PATH_ASSIGNMENTS}/${id}` })
        .then(json => json.data)
        .then(assignment => {
          commit('updateStatus', { status: COMPLETE })
          commit('setAssignment', assignment)
          return assignment
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveAssignment: ({ state, commit, dispatch }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving contract, please wait...' })

      const actionName = state.id ? 'update' : 'save'

      return dispatch(actionName).then(response => {
        commit('updateStatus', { status: COMPLETE })

        if (!response.data) {
          throw new Error('Unable to save contract. Check required fields.')
        }

        commit('clearAssignment')
        notifySuccess(dispatch, 'Contract saved')
        return response.data
      }).catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, 'Unable to save contract. Try again later')

        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    deleteAssignment ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting contract, please wait...' })
      return api.doDelete({ path: `${PATH_ASSIGNMENTS}/${id}` })
        .then(response => {
          if (!response.data) {
            throw new Error('Unable to delete contract')
          } else {
            commit('updateStatus', { status: COMPLETE })
            notifySuccess(dispatch, 'Contract deleted')
          }
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete contract')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearAssignment: ({ commit }) => {
      commit('clearAssignment')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default assignment
